/**
 * HMC Store Locator — Mapbox GL JS, lazy init when visible.
 * Data: optional Cloudflare worker (Mapbox Dataset → JSON), then theme asset hmc-installers.json (fallback).
 * Token: Theme settings → HMC store locator (Mapbox) → public token (pk.) for Mapbox GL only.
 */
(function () {
  var MAPBOX_JS = 'https://api.mapbox.com/mapbox-gl-js/v3.6.0/mapbox-gl.js';
  var MAPBOX_CSS = 'https://api.mapbox.com/mapbox-gl-js/v3.6.0/mapbox-gl.css';
  var DEFAULT_STYLE = 'mapbox://styles/mapbox/streets-v12';
  /** Worker / dataset feed: wait this long, then fall back to hmc-installers.json. */
  var INSTALLER_FEED_TIMEOUT_MS = 6000;
  /** Find an Installer: remember location opt-in (localStorage). */
  var HMC_INSTALLER_LOC_STORAGE_KEY = 'hmc_find_installer_location_v1';
  /** Session cache for worker feed + ETag (304 saves bandwidth; Mapbox cost is on the worker). */
  var HMC_INSTALLER_FEED_CACHE_PREFIX = 'hmc_installers_feed_v3:';

  function installerFeedCacheKeys(url) {
    return {
      body: HMC_INSTALLER_FEED_CACHE_PREFIX + url + ':body',
      etag: HMC_INSTALLER_FEED_CACHE_PREFIX + url + ':etag',
    };
  }

  function readInstallerFeedSession(url) {
    try {
      var keys = installerFeedCacheKeys(url);
      var body = sessionStorage.getItem(keys.body);
      var etag = sessionStorage.getItem(keys.etag);
      if (body && etag) return { body: body, etag: etag };
    } catch (e) {}
    return null;
  }

  function writeInstallerFeedSession(url, body, etag) {
    try {
      var keys = installerFeedCacheKeys(url);
      sessionStorage.setItem(keys.body, body);
      if (etag) sessionStorage.setItem(keys.etag, etag);
    } catch (e) {}
  }

  function loadCss(href) {
    if (document.querySelector('link[data-hmc-mapbox-css="1"]')) return Promise.resolve();
    return new Promise(function (resolve, reject) {
      var l = document.createElement('link');
      l.rel = 'stylesheet';
      l.href = href;
      l.setAttribute('data-hmc-mapbox-css', '1');
      l.onload = function () {
        resolve();
      };
      l.onerror = function () {
        reject(new Error('Mapbox CSS failed'));
      };
      document.head.appendChild(l);
    });
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (window.mapboxgl) {
        resolve();
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () {
        resolve();
      };
      s.onerror = function () {
        reject(new Error('Mapbox JS failed'));
      };
      document.head.appendChild(s);
    });
  }

  function haversineMiles(lat1, lng1, lat2, lng2) {
    var R = 3958.8;
    var dLat = ((lat2 - lat1) * Math.PI) / 180;
    var dLng = ((lng2 - lng1) * Math.PI) / 180;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function escapeHtml(s) {
    if (s == null || s === '') return '';
    var d = document.createElement('div');
    d.textContent = String(s);
    return d.innerHTML;
  }

  function isElementVisible(el) {
    if (!el || !el.isConnected) return false;
    var r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) return false;
    var st = window.getComputedStyle(el);
    if (st.display === 'none' || st.visibility === 'hidden' || Number(st.opacity) === 0) return false;
    return true;
  }

  /** Mobile / tablet: 5 per page + pager. Desktop (≥1000px): scroll full filtered list in the panel. */
  var PDP_LIST_PAGE_SIZE = 5;
  var PDP_DESKTOP_SCROLL_MEDIA = '(min-width: 1000px)';

  function isPdpInstallerTabContext(root) {
    if (!root) return false;
    return !!(
      root.closest('.hmc-pdp-tabs__panel--installer') ||
      root.closest('[data-hmc-installer-mount]') ||
      root.closest('.hmc-pdp-installer-tab__mount')
    );
  }

  function parseInstallers(text) {
    var data = JSON.parse(text);
    if (data && typeof data === 'object' && !Array.isArray(data) && data.error) {
      throw new Error(String(data.error));
    }
    if (!Array.isArray(data)) return [];
    return data
      .map(function (r) {
        if (!r) return null;
        var lat = Number(r.lat);
        var lng = Number(r.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        r.lat = lat;
        r.lng = lng;
        return r;
      })
      .filter(Boolean);
  }

  /**
   * Fetch JSON from url; credentials: 'omit' for cross-origin worker, 'same-origin' for theme asset.
   * Worker feed: sends If-None-Match; 304 reuses sessionStorage body (no Mapbox hit on worker when ETag matches).
   */
  function fetchInstallersJson(url, credentials, signal) {
    var opts = { cache: 'no-store', credentials: credentials };
    if (signal) opts.signal = signal;
    var useConditional = credentials === 'omit';
    var sessionHit = useConditional ? readInstallerFeedSession(url) : null;
    if (useConditional && sessionHit && sessionHit.etag) {
      opts.headers = { 'If-None-Match': sessionHit.etag };
    }
    return fetch(url, opts)
      .then(function (r) {
        if (r.status === 304) {
          if (sessionHit && sessionHit.body) return parseInstallers(sessionHit.body);
          throw new Error('Installers list unchanged but session cache missing — refresh the page.');
        }
        if (!r.ok) throw new Error('Could not load installers list (' + r.status + ').');
        return r.text().then(function (text) {
          if (useConditional) {
            writeInstallerFeedSession(url, text, r.headers.get('ETag') || r.headers.get('etag') || '');
          }
          return parseInstallers(text);
        });
      });
  }

  function fetchFallbackRows(fallbackUrl) {
    return fetchInstallersJson(fallbackUrl, 'same-origin').then(function (fb) {
      return fb && fb.length ? fb : [];
    });
  }

  /**
   * Prefer dataset feed when configured. After INSTALLER_FEED_TIMEOUT_MS without a usable list,
   * abort the feed request and load hmc-installers.json. Also uses JSON on any feed error or empty rows.
   * @param {string} feedUrl optional worker URL
   * @param {string} fallbackUrl theme asset URL
   */
  function loadInstallerRows(feedUrl, fallbackUrl) {
    if (!feedUrl) {
      return fetchFallbackRows(fallbackUrl);
    }

    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var tmr = null;
    if (ctrl) {
      tmr = setTimeout(function () {
        try {
          ctrl.abort();
        } catch (e) {}
      }, INSTALLER_FEED_TIMEOUT_MS);
    }

    function clearFeedTimer() {
      if (tmr) {
        clearTimeout(tmr);
        tmr = null;
      }
    }

    return fetchInstallersJson(feedUrl, 'omit', ctrl ? ctrl.signal : undefined)
      .then(function (rows) {
        clearFeedTimer();
        if (rows && rows.length) return rows;
        return fetchFallbackRows(fallbackUrl).then(function (fb) {
          return fb && fb.length ? fb : rows || [];
        });
      })
      .catch(function () {
        clearFeedTimer();
        return fetchFallbackRows(fallbackUrl);
      });
  }

  function buildPopupHtml(row) {
    var lines = [];
    var addr = [row.address, [row.city, row.state].filter(Boolean).join(', '), row.zip].filter(Boolean).join(' · ');
    if (addr) lines.push('<p class="hmc-store-locator__pop-line">' + escapeHtml(addr) + '</p>');
    if (row.phone)
      lines.push(
        '<p class="hmc-store-locator__pop-line"><a href="tel:' +
          String(row.phone).replace(/[^\d+]/g, '') +
          '">' +
          escapeHtml(row.phone) +
          '</a></p>'
      );
    if (row.email)
      lines.push(
        '<p class="hmc-store-locator__pop-line"><a href="mailto:' +
          escapeHtml(row.email) +
          '">' +
          escapeHtml(row.email) +
          '</a></p>'
      );
    if (row.url)
      lines.push(
        '<p class="hmc-store-locator__pop-line"><a href="' +
          escapeHtml(row.url) +
          '" target="_blank" rel="noopener">Website</a></p>'
      );
    var dest = row.lat + ',' + row.lng;
    var dir =
      'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(dest);
    lines.push(
      '<div class="hmc-store-locator__pop-actions"><a href="' +
        dir +
        '" target="_blank" rel="noopener">Directions</a></div>'
    );
    var title =
      escapeHtml(row.name || 'Installer') +
      (row.isNew ? ' <span class="hmc-store-locator__card-new">New</span>' : '');
    return (
      '<div class="hmc-store-locator__pop-inner"><h3 class="hmc-store-locator__pop-title">' +
      title +
      '</h3>' +
      lines.join('') +
      '</div>'
    );
  }

  function StoreLocatorRoot(root) {
    this.root = root;
    this.token = (root.getAttribute('data-mapbox-token') || '').trim();
    this.feedUrl = (root.getAttribute('data-installers-feed-url') || '').trim();
    this.fallbackUrl = (
      (root.getAttribute('data-installers-fallback-url') || root.getAttribute('data-installers-url') || '') + ''
    ).trim();
    this.mapEl = root.querySelector('[data-hmc-sl-map]');
    this.listEl = root.querySelector('[data-hmc-sl-list]');
    this.searchEl = root.querySelector('[data-hmc-sl-search]');
    this.radiusEl = root.querySelector('[data-hmc-sl-radius]');
    this.radiusLabel = root.querySelector('[data-hmc-sl-radius-val]');
    this.btnLoc = root.querySelector('[data-hmc-sl-locate]');
    this.loadingEl = root.querySelector('[data-hmc-sl-map-loading]');
    this.rows = [];
    this.markers = [];
    this.map = null;
    this.userLat = null;
    this.userLng = null;
    this.popup = null;
    this.mapStyle = root.getAttribute('data-map-style') || DEFAULT_STYLE;
    this._filterTimer = null;
    this.searchPlaceLat = null;
    this.searchPlaceLng = null;
    this._geocodePlaceLabel = '';
    this._suggestTimer = null;
    this._suggestAbort = null;
    this._suggestHighlight = -1;
    this._lastSuggestFeatures = [];
    this.suggestionsEl = root.querySelector('[data-hmc-sl-suggestions]');
    this.searchWrapEl = root.querySelector('[data-hmc-sl-search-wrap]');
    this.pagedList =
      isPdpInstallerTabContext(root) ||
      !!(root && root.getAttribute('data-hmc-sl-paged') === '1');
    this.listPage = 0;
    this.listPageSize = PDP_LIST_PAGE_SIZE;
    this._listFilterKey = '';
    this.pagerEl = null;
    this.pagerMetaEl = null;
    this.pagerPrevBtn = null;
    this.pagerNextBtn = null;
    this._layoutWatchBound = false;
    this.root._hmcStoreLocatorInstance = this;
  }

  StoreLocatorRoot.prototype.isDesktopScrollList = function () {
    return (
      this.pagedList &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(PDP_DESKTOP_SCROLL_MEDIA).matches
    );
  };

  /** Pagination only on narrow viewports; desktop shows the full filtered list in a scroll panel. */
  StoreLocatorRoot.prototype.usesPagedList = function () {
    return this.pagedList && !this.isDesktopScrollList();
  };

  StoreLocatorRoot.prototype.syncListLayoutMode = function () {
    if (!this.pagedList) return;
    var desktop = this.isDesktopScrollList();
    this.root.classList.toggle('hmc-store-locator--paged-list', !desktop);
    this.root.classList.toggle('hmc-store-locator--desktop-scroll', desktop);
    if (desktop) {
      this.listPage = 0;
      this.listPageSize = PDP_LIST_PAGE_SIZE;
      this.root.removeAttribute('data-hmc-sl-page-size');
      if (this.pagerEl) this.pagerEl.hidden = true;
    }
  };

  StoreLocatorRoot.prototype.bindListLayoutWatch = function () {
    var self = this;
    if (!self.pagedList || self._layoutWatchBound) return;
    self._layoutWatchBound = true;
    self.syncListLayoutMode();

    if (typeof window.matchMedia !== 'function') return;
    var mq = window.matchMedia(PDP_DESKTOP_SCROLL_MEDIA);
    var onMq = function () {
      self.syncListLayoutMode();
      if (self.usesPagedList() && !self.pagerEl) self.ensurePager();
      self.renderList();
      self.scheduleMapResize();
    };
    if (mq.addEventListener) mq.addEventListener('change', onMq);
    else if (mq.addListener) mq.addListener(onMq);
  };

  StoreLocatorRoot.prototype.setLoading = function (on) {
    if (!this.loadingEl) return;
    if (on) this.loadingEl.removeAttribute('hidden');
    else this.loadingEl.setAttribute('hidden', '');
  };

  StoreLocatorRoot.prototype.showError = function (msg) {
    if (this.listEl) {
      this.listEl.innerHTML =
        '<p class="hmc-store-locator__error" role="alert">' + escapeHtml(msg) + '</p>';
    }
    this.setLoading(false);
  };

  StoreLocatorRoot.prototype.destroyMap = function () {
    delete this.root._hmcMapboxMap;
    if (this.popup) {
      try {
        this.popup.remove();
      } catch (e) {}
      this.popup = null;
    }
    this.markers.forEach(function (m) {
      try {
        m.remove();
      } catch (e) {}
    });
    this.markers = [];
    if (this.map) {
      try {
        this.map.remove();
      } catch (e) {}
      this.map = null;
    }
    this.root.dataset.hmcLocatorInit = '';
  };

  StoreLocatorRoot.prototype.init = function () {
    var self = this;
    if (self.root.dataset.hmcLocatorInit === '1') return;
    if (!self.token) {
      self.showError('Add your Mapbox public token under Theme settings → HMC store locator (Mapbox).');
      return;
    }
    if (!self.fallbackUrl) {
      self.showError('Missing installers fallback URL (theme asset).');
      return;
    }

    self.root.dataset.hmcLocatorInit = '1';
    self.setLoading(true);

    loadInstallerRows(self.feedUrl, self.fallbackUrl)
      .then(function (rows) {
        self.rows = rows;
        if (!self.rows.length) {
          self.showError(
            'No installers with valid coordinates. Update the Mapbox dataset or add lat/lng rows to hmc-installers.json.'
          );
          return;
        }
        return loadCss(MAPBOX_CSS).then(function () {
          return loadScript(MAPBOX_JS);
        }).then(function () {
          window.mapboxgl.accessToken = self.token;
          if (!self.mapEl) throw new Error('Map container missing');
          var pocketMap =
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(max-width: 999px)').matches;
          self.map = new window.mapboxgl.Map({
            container: self.mapEl,
            style: self.mapStyle,
            projection: 'mercator',
            center: [self.rows[0].lng, self.rows[0].lat],
            zoom: 5,
            attributionControl: true,
            cooperativeGestures: pocketMap,
          });
          self.map.addControl(new window.mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
          self.popup = new window.mapboxgl.Popup({ closeButton: true, maxWidth: '300px' });
          self.root._hmcMapboxMap = self.map;
          try {
            self.map.resize();
            setTimeout(function () {
              try {
                self.map.resize();
              } catch (e) {}
            }, 80);
          } catch (e) {}

          self.rows.forEach(function (row) {
            var el = document.createElement('button');
            el.type = 'button';
            el.className = 'hmc-store-locator__map-marker';
            el.style.width = '28px';
            el.style.height = '28px';
            el.style.borderRadius = '50%';
            el.style.border = '2px solid #fff';
            el.style.background = '#38B5FF';
            el.style.cursor = 'pointer';
            el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.35)';
            el.style.zIndex = '3';
            el.style.pointerEvents = 'auto';
            el.style.touchAction = 'manipulation';
            el.setAttribute('aria-label', row.name || 'Installer');
            el.addEventListener('click', function (ev) {
              if (ev) {
                ev.preventDefault();
                ev.stopPropagation();
              }
              self.focusInstaller(row);
            });

            var marker = new window.mapboxgl.Marker({ element: el }).setLngLat([row.lng, row.lat]).addTo(self.map);
            marker._hmcRowId = row.id;
            self.markers.push(marker);
          });

          self.fitAll();
          self.bindUi();
          self.bindListLayoutWatch();
          if (self.usesPagedList()) self.ensurePager();
          self.renderList();
          self.setLoading(false);
          self.maybeAutoLocateOnLoad();
        });
      })
      .catch(function (err) {
        self.showError(err.message || 'Map failed to load.');
        self.root.dataset.hmcLocatorInit = '';
      });
  };

  StoreLocatorRoot.prototype.fitAll = function () {
    if (!this.map) return;
    // Default to continental US so customers do not start on a world view.
    var b = new window.mapboxgl.LngLatBounds(
      [-125, 24], // southwest
      [-66, 49] // northeast
    );
    try {
      this.map.fitBounds(b, { padding: 36, maxZoom: 6, duration: 0 });
    } catch (e) {}
  };

  /** Mapbox Geocoding place pick takes precedence over GPS for list radius / sort. */
  StoreLocatorRoot.prototype.getRefCoords = function () {
    if (this.searchPlaceLat != null && this.searchPlaceLng != null) {
      return { lat: this.searchPlaceLat, lng: this.searchPlaceLng };
    }
    if (this.userLat != null && this.userLng != null) {
      return { lat: this.userLat, lng: this.userLng };
    }
    return null;
  };

  StoreLocatorRoot.prototype.clearSearchPlace = function () {
    this.searchPlaceLat = null;
    this.searchPlaceLng = null;
    this._geocodePlaceLabel = '';
  };

  StoreLocatorRoot.prototype.syncSearchAriaExpanded = function (open) {
    if (!this.searchEl) return;
    if (open) this.searchEl.setAttribute('aria-expanded', 'true');
    else this.searchEl.setAttribute('aria-expanded', 'false');
  };

  StoreLocatorRoot.prototype.hideSuggestions = function () {
    if (!this.suggestionsEl) return;
    this.suggestionsEl.innerHTML = '';
    this.suggestionsEl.setAttribute('hidden', '');
    this._suggestHighlight = -1;
    this.syncSearchAriaExpanded(false);
  };

  StoreLocatorRoot.prototype.fetchLocationSuggestions = function (q, signal) {
    var token = this.token;
    if (!token || !q || q.length < 2) return Promise.resolve([]);
    var url =
      'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
      encodeURIComponent(q) +
      '.json?access_token=' +
      encodeURIComponent(token) +
      '&autocomplete=true&types=place,locality,neighborhood,region,postcode,address,district' +
      '&country=US&limit=8&language=en';
    return fetch(url, { signal: signal, cache: 'no-store' }).then(function (r) {
      if (!r.ok) throw new Error('geocode');
      return r.json();
    }).then(function (j) {
      return j.features || [];
    });
  };

  StoreLocatorRoot.prototype.renderSuggestions = function (features) {
    var self = this;
    if (!self.suggestionsEl) return;
    self._lastSuggestFeatures = features || [];
    self._suggestHighlight = -1;
    self.suggestionsEl.innerHTML = '';
    if (!features.length) {
      self.hideSuggestions();
      return;
    }
    self.suggestionsEl.removeAttribute('hidden');
    self.syncSearchAriaExpanded(true);
    features.forEach(function (f, idx) {
      var li = document.createElement('li');
      li.className = 'hmc-store-locator__suggestion';
      li.setAttribute('role', 'presentation');
      li.addEventListener('mousedown', function (ev) {
        ev.preventDefault();
      });
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'hmc-store-locator__suggestion-btn';
      btn.setAttribute('role', 'option');
      btn.setAttribute('id', 'hmc-sl-sug-' + idx);
      btn.setAttribute('tabindex', '-1');
      var main = escapeHtml(f.place_name || f.text || '');
      btn.innerHTML = '<span class="hmc-store-locator__suggestion-main">' + main + '</span>';
      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        self.applySelectedFeature(f);
      });
      li.appendChild(btn);
      self.suggestionsEl.appendChild(li);
    });
  };

  StoreLocatorRoot.prototype.applySelectedFeature = function (feature) {
    var coords = feature.center || (feature.geometry && feature.geometry.coordinates);
    if (!coords || coords.length < 2) return;
    var lng = Number(coords[0]);
    var lat = Number(coords[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    var label = (feature.place_name || feature.text || '').trim();
    this.searchPlaceLat = lat;
    this.searchPlaceLng = lng;
    this._geocodePlaceLabel = label;
    if (this.searchEl) this.searchEl.value = label;
    this.hideSuggestions();
    this.fitMapToUserAndNearest();
    this.renderList();
  };

  StoreLocatorRoot.prototype.scheduleSuggestions = function () {
    var self = this;
    if (!self.suggestionsEl || !self.searchEl) return;
    if (self._suggestTimer) window.clearTimeout(self._suggestTimer);
    if (self._suggestAbort) {
      try {
        self._suggestAbort.abort();
      } catch (e) {}
      self._suggestAbort = null;
    }
    var raw = (self.searchEl.value || '').trim();
    if (raw.length < 2) {
      self.hideSuggestions();
      return;
    }
    self._suggestTimer = window.setTimeout(function () {
      self._suggestTimer = null;
      if (typeof AbortController !== 'undefined') self._suggestAbort = new AbortController();
      var sig = self._suggestAbort ? self._suggestAbort.signal : undefined;
      self
        .fetchLocationSuggestions(raw, sig)
        .then(function (features) {
          self.renderSuggestions(features);
        })
        .catch(function () {
          self.hideSuggestions();
        });
    }, 220);
  };

  StoreLocatorRoot.prototype.getRadiusMiles = function () {
    return this.radiusEl ? Number(this.radiusEl.value) || 150 : 150;
  };

  /** After layout changes (e.g. closing a modal), resize map then run camera update. */
  StoreLocatorRoot.prototype._runWhenMapReady = function (fn) {
    var self = this;
    if (!self.map) return;
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        try {
          self.map.resize();
        } catch (e) {}
        window.setTimeout(function () {
          try {
            self.map.resize();
          } catch (e2) {}
          if (typeof fn === 'function') fn();
        }, 50);
      });
    });
  };

  /** Fit map to user + installers within current radius slider; fly to user if bounds are unreliable. */
  StoreLocatorRoot.prototype.fitMapToUserAndNearest = function () {
    var self = this;
    var ref = self.getRefCoords();
    if (!self.map || !ref) return;
    var ulat = ref.lat;
    var ulng = ref.lng;
    if (!Number.isFinite(ulat) || !Number.isFinite(ulng)) return;

    var maxMi = self.getRadiusMiles();
    var nearby = self.rows.filter(function (r) {
      return haversineMiles(ulat, ulng, r.lat, r.lng) <= maxMi;
    });

    var applyCamera = function () {
      if (!nearby.length) {
        try {
          self.map.flyTo({
            center: [ulng, ulat],
            zoom: 10,
            duration: 700,
          });
        } catch (e) {
          try {
            self.map.jumpTo({ center: [ulng, ulat], zoom: 10 });
          } catch (e2) {}
        }
        return;
      }
      var b = new window.mapboxgl.LngLatBounds();
      b.extend([ulng, ulat]);
      nearby.forEach(function (r) {
        b.extend([r.lng, r.lat]);
      });
      try {
        self.map.fitBounds(b, {
          padding: { top: 52, right: 52, bottom: 80, left: 52 },
          maxZoom: 12,
          duration: 800,
        });
      } catch (e) {
        try {
          self.map.flyTo({ center: [ulng, ulat], zoom: 10, duration: 650 });
        } catch (e2) {}
      }
    };

    self._runWhenMapReady(applyCamera);
  };

  /** If nothing falls within radius, widen slider to 500 mi once (auto-locate only). */
  StoreLocatorRoot.prototype.widenRadiusIfNoMatches = function () {
    if (!this.radiusEl || !this.radiusLabel) return;
    if (this.filteredRows().length > 0) return;
    var v = Number(this.radiusEl.value) || 150;
    if (v >= 500) return;
    this.radiusEl.value = '500';
    this.radiusLabel.textContent = '500 mi';
    try {
      this.radiusEl.dispatchEvent(new Event('input', { bubbles: true }));
    } catch (e) {}
  };

  StoreLocatorRoot.prototype.applyGeolocationCoords = function (lat, lng, opts) {
    opts = opts || {};
    this.clearSearchPlace();
    if (this.searchEl) this.searchEl.value = '';
    this.userLat = lat;
    this.userLng = lng;
    if (opts.widenIfEmpty !== false) this.widenRadiusIfNoMatches();
    this.renderList();
    this.fitMapToUserAndNearest();
  };

  StoreLocatorRoot.prototype.removeLocationConsentModal = function () {
    if (this._locModalEscHandler) {
      try {
        document.removeEventListener('keydown', this._locModalEscHandler, true);
      } catch (e) {}
      this._locModalEscHandler = null;
    }
    if (this._locModalEl && this._locModalEl.parentNode) {
      this._locModalEl.parentNode.removeChild(this._locModalEl);
    }
    this._locModalEl = null;
    try {
      document.body.style.overflow = '';
    } catch (e) {}
  };

  /** First visit on Find an Installer: in-page prompt, then browser geolocation permission. */
  StoreLocatorRoot.prototype.showLocationConsentModal = function () {
    var self = this;
    if (document.querySelector('.hmc-store-locator__loc-modal-backdrop')) return;
    var backdrop = document.createElement('div');
    backdrop.className = 'hmc-store-locator__loc-modal-backdrop';
    backdrop.setAttribute('role', 'presentation');

    var dialog = document.createElement('div');
    dialog.className = 'hmc-store-locator__loc-modal';
    dialog.addEventListener('click', function (ev) {
      ev.stopPropagation();
    });
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'hmc-loc-modal-title');
    dialog.setAttribute('tabindex', '-1');

    var title = document.createElement('h2');
    title.id = 'hmc-loc-modal-title';
    title.className = 'hmc-store-locator__loc-modal-title';
    title.textContent = 'Show installers near you?';

    var p = document.createElement('p');
    p.className = 'hmc-store-locator__loc-modal-text';
    p.textContent =
      'Allow location access so we can zoom the map to your area and sort shops by distance. You can change this anytime in your browser settings.';

    var actions = document.createElement('div');
    actions.className = 'hmc-store-locator__loc-modal-actions';

    var btnSkip = document.createElement('button');
    btnSkip.type = 'button';
    btnSkip.className = 'hmc-store-locator__loc-modal-btn hmc-store-locator__loc-modal-btn--secondary';
    btnSkip.textContent = 'Not now';

    var btnAllow = document.createElement('button');
    btnAllow.type = 'button';
    btnAllow.className = 'hmc-store-locator__loc-modal-btn hmc-store-locator__loc-modal-btn--primary';
    btnAllow.textContent = 'Allow location';

    function dismissSkipped() {
      try {
        localStorage.setItem(HMC_INSTALLER_LOC_STORAGE_KEY, 'dismissed');
      } catch (err) {}
      self.removeLocationConsentModal();
    }

    btnSkip.addEventListener('click', dismissSkipped);

    btnAllow.addEventListener('click', function () {
      if (!navigator.geolocation || typeof navigator.geolocation.getCurrentPosition !== 'function') {
        dismissSkipped();
        return;
      }
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          try {
            localStorage.setItem(HMC_INSTALLER_LOC_STORAGE_KEY, 'granted');
          } catch (err) {}
          self.removeLocationConsentModal();
          var la = pos.coords.latitude;
          var ln = pos.coords.longitude;
          window.setTimeout(function () {
            self.applyGeolocationCoords(la, ln, { widenIfEmpty: true });
          }, 100);
        },
        function () {
          dismissSkipped();
        },
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
      );
    });

    self._locModalEscHandler = function (ev) {
      if (ev.key !== 'Escape') return;
      dismissSkipped();
    };
    document.addEventListener('keydown', self._locModalEscHandler, true);

    backdrop.addEventListener('click', function (ev) {
      if (ev.target === backdrop) dismissSkipped();
    });

    actions.appendChild(btnSkip);
    actions.appendChild(btnAllow);
    dialog.appendChild(title);
    dialog.appendChild(p);
    dialog.appendChild(actions);
    backdrop.appendChild(dialog);
    document.body.appendChild(backdrop);
    self._locModalEl = backdrop;
    try {
      document.body.style.overflow = 'hidden';
    } catch (e) {}
    window.setTimeout(function () {
      try {
        btnAllow.focus();
      } catch (e2) {}
    }, 50);
  };

  /**
   * Find an Installer: first visit shows consent modal → browser permission.
   * Returning visitors with `granted` get silent geolocation; `dismissed` skips auto-locate.
   */
  StoreLocatorRoot.prototype.maybeAutoLocateOnLoad = function () {
    var self = this;
    if (self.root.getAttribute('data-hmc-sl-auto-locate') !== '1') return;
    if (!navigator.geolocation || typeof navigator.geolocation.getCurrentPosition !== 'function') return;

    var pref = '';
    try {
      pref = localStorage.getItem(HMC_INSTALLER_LOC_STORAGE_KEY) || '';
    } catch (e) {}

    if (pref === 'granted') {
      window.setTimeout(function () {
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            var la = pos.coords.latitude;
            var ln = pos.coords.longitude;
            window.setTimeout(function () {
              self.applyGeolocationCoords(la, ln, { widenIfEmpty: true });
            }, 80);
          },
          function () {},
          { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
        );
      }, 500);
      return;
    }

    if (pref === 'dismissed') return;

    window.setTimeout(function () {
      self.showLocationConsentModal();
    }, 400);
  };

  StoreLocatorRoot.prototype.openPopup = function (row) {
    if (!this.map || !this.popup) return;
    this.popup.setLngLat([row.lng, row.lat]).setHTML(buildPopupHtml(row)).addTo(this.map);
  };

  /** Zoom to installer and show popup (map markers + list rows). */
  StoreLocatorRoot.prototype.focusInstaller = function (row) {
    if (!this.map || !row) return;
    var z = Math.max(this.map.getZoom(), 12);
    try {
      this.map.flyTo({ center: [row.lng, row.lat], zoom: z, duration: 550 });
    } catch (e) {
      try {
        this.map.jumpTo({ center: [row.lng, row.lat], zoom: z });
      } catch (e2) {}
    }
    this.openPopup(row);
  };

  StoreLocatorRoot.prototype._updateSuggestHighlight = function () {
    if (!this.suggestionsEl) return;
    var btns = this.suggestionsEl.querySelectorAll('.hmc-store-locator__suggestion-btn');
    for (var i = 0; i < btns.length; i++) {
      btns[i].classList.toggle('is-active', i === this._suggestHighlight);
    }
  };

  StoreLocatorRoot.prototype.bindUi = function () {
    var self = this;
    if (self.searchEl) {
      self.searchEl.addEventListener(
        'input',
        function () {
          var v = (self.searchEl.value || '').trim();
          if (!v) {
            self.clearSearchPlace();
            self.hideSuggestions();
          } else if (self._geocodePlaceLabel && v !== self._geocodePlaceLabel) {
            self.clearSearchPlace();
          }
          if (self._filterTimer) window.clearTimeout(self._filterTimer);
          self._filterTimer = window.setTimeout(function () {
            self.renderList();
          }, 120);
          self.scheduleSuggestions();
        },
        { passive: true }
      );

      self.searchEl.addEventListener('keydown', function (ev) {
        if (!self.suggestionsEl || self.suggestionsEl.hasAttribute('hidden')) return;
        var feats = self._lastSuggestFeatures || [];
        if (!feats.length) return;
        if (ev.key === 'Escape') {
          ev.preventDefault();
          self.hideSuggestions();
          return;
        }
        if (ev.key === 'ArrowDown') {
          ev.preventDefault();
          self._suggestHighlight = Math.min(self._suggestHighlight + 1, feats.length - 1);
          self._updateSuggestHighlight();
        } else if (ev.key === 'ArrowUp') {
          ev.preventDefault();
          self._suggestHighlight = Math.max(self._suggestHighlight - 1, 0);
          self._updateSuggestHighlight();
        } else if (ev.key === 'Enter' && self._suggestHighlight >= 0) {
          ev.preventDefault();
          self.applySelectedFeature(feats[self._suggestHighlight]);
        }
      });
    }

    if (self.searchWrapEl && !self._docClickBound) {
      self._docClickBound = function (ev) {
        if (!self.searchWrapEl || !ev.target) return;
        if (!self.searchWrapEl.contains(ev.target)) self.hideSuggestions();
      };
      document.addEventListener('click', self._docClickBound, true);
    }
    if (self.radiusEl) {
      self.radiusEl.addEventListener('input', function () {
        if (self.radiusLabel) {
          self.radiusLabel.textContent = self.radiusEl.value + ' mi';
        }
        self.renderList();
      });
    }
    if (self.btnLoc) {
      self.btnLoc.addEventListener('click', function () {
        if (!navigator.geolocation) {
          window.alert('Location is not supported in this browser.');
          return;
        }
        self.btnLoc.disabled = true;
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            self.btnLoc.disabled = false;
            self.applyGeolocationCoords(pos.coords.latitude, pos.coords.longitude, { widenIfEmpty: false });
          },
          function () {
            self.btnLoc.disabled = false;
            window.alert('Could not read your location. Allow location for this site and try again.');
          },
          { enableHighAccuracy: false, timeout: 12000, maximumAge: 600000 }
        );
      });
    }
  };

  StoreLocatorRoot.prototype.filteredRows = function () {
    var q = (this.searchEl && this.searchEl.value ? this.searchEl.value : '').trim().toLowerCase();
    var maxMi = this.radiusEl ? Number(this.radiusEl.value) || 500 : 500;
    var self = this;
    var ref = self.getRefCoords();
    var placePinned = self.searchPlaceLat != null;

    return this.rows.filter(function (r) {
      if (ref) {
        var d = haversineMiles(ref.lat, ref.lng, r.lat, r.lng);
        if (d > maxMi) return false;
      }
      if (q && (!ref || !placePinned)) {
        var blob = [r.name, r.address, r.city, r.state, r.zip, r.phone, r.email]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (blob.indexOf(q) === -1) return false;
      }
      return true;
    });
  };

  StoreLocatorRoot.prototype.getListFilterKey = function () {
    var q = this.searchEl && this.searchEl.value ? this.searchEl.value.trim() : '';
    var maxMi = this.getRadiusMiles();
    var ref = this.getRefCoords();
    return (
      String(maxMi) +
      '|' +
      q +
      '|' +
      (ref ? ref.lat.toFixed(4) + ',' + ref.lng.toFixed(4) : '') +
      '|' +
      (this.searchPlaceLat != null ? 'place' : '')
    );
  };

  StoreLocatorRoot.prototype.getSortedFilteredRows = function () {
    var rows = this.filteredRows().slice();
    var refSort = this.getRefCoords();
    rows.sort(function (a, b) {
      if (!refSort) return (a.name || '').localeCompare(b.name || '');
      var da = haversineMiles(refSort.lat, refSort.lng, a.lat, a.lng);
      var db = haversineMiles(refSort.lat, refSort.lng, b.lat, b.lng);
      return da - db;
    });
    return rows;
  };

  StoreLocatorRoot.prototype.ensurePager = function () {
    if (!this.pagedList || this.pagerEl || !this.listEl) return;
    var panel = this.listEl.parentElement;
    if (!panel) return;

    var pager = document.createElement('div');
    pager.className = 'hmc-store-locator__pager';
    pager.setAttribute('data-hmc-sl-pager', '');
    pager.hidden = true;
    pager.innerHTML =
      '<p class="hmc-store-locator__pager-meta" data-hmc-sl-pager-meta aria-live="polite"></p>' +
      '<div class="hmc-store-locator__pager-actions">' +
      '<button type="button" class="hmc-store-locator__pager-btn" data-hmc-sl-pager-prev>Previous</button>' +
      '<button type="button" class="hmc-store-locator__pager-btn hmc-store-locator__pager-btn--primary" data-hmc-sl-pager-next>Next</button>' +
      '</div>';

    this.pagerEl = pager;
    this.pagerMetaEl = pager.querySelector('[data-hmc-sl-pager-meta]');
    this.pagerPrevBtn = pager.querySelector('[data-hmc-sl-pager-prev]');
    this.pagerNextBtn = pager.querySelector('[data-hmc-sl-pager-next]');

    var self = this;
    if (this.pagerPrevBtn) {
      this.pagerPrevBtn.addEventListener('click', function () {
        self.goListPage(self.listPage - 1, 'prev');
      });
    }
    if (this.pagerNextBtn) {
      this.pagerNextBtn.addEventListener('click', function () {
        self.goListPage(self.listPage + 1, 'next');
      });
    }

    panel.appendChild(pager);
  };

  StoreLocatorRoot.prototype.updatePager = function (total, startIndex, shownCount, totalPages) {
    if (!this.pagerEl) return;
    if (totalPages <= 1) {
      this.pagerEl.hidden = true;
      return;
    }

    this.pagerEl.hidden = false;
    var from = startIndex + 1;
    var to = startIndex + shownCount;
    if (this.pagerMetaEl) {
      this.pagerMetaEl.textContent = 'Showing ' + from + '–' + to + ' of ' + total + ' installers';
    }
    if (this.pagerPrevBtn) {
      this.pagerPrevBtn.disabled = this.listPage <= 0;
      this.pagerPrevBtn.textContent =
        this.listPage <= 1 ? 'Previous' : 'Previous ' + this.listPageSize;
    }
    if (this.pagerNextBtn) {
      this.pagerNextBtn.disabled = this.listPage >= totalPages - 1;
      var remaining = total - to;
      var nextCount = Math.min(this.listPageSize, remaining);
      this.pagerNextBtn.textContent =
        nextCount >= this.listPageSize ? 'Next ' + this.listPageSize : 'Next ' + nextCount;
    }
  };

  StoreLocatorRoot.prototype.scrollListIntoView = function (direction) {
    var anchor = this.listEl;
    if (!anchor) return;
    var block = direction === 'prev' ? 'start' : 'nearest';
    try {
      anchor.scrollIntoView({ behavior: 'smooth', block: block });
    } catch (e) {
      try {
        anchor.scrollIntoView(true);
      } catch (e2) {}
    }
  };

  StoreLocatorRoot.prototype.goListPage = function (page, direction) {
    var rows = this.getSortedFilteredRows();
    var totalPages = Math.max(1, Math.ceil(rows.length / this.listPageSize));
    var nextPage = Math.max(0, Math.min(page, totalPages - 1));
    if (nextPage === this.listPage) return;
    this.listPage = nextPage;
    this.renderList({ scroll: direction || 'next' });
  };

  StoreLocatorRoot.prototype.appendListCard = function (r) {
    var self = this;
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hmc-store-locator__card';
    btn.setAttribute('data-id', r.id || '');
    btn.setAttribute('role', 'listitem');
    var distHtml = '';
    var refD = self.getRefCoords();
    if (refD) {
      var mi = haversineMiles(refD.lat, refD.lng, r.lat, r.lng);
      distHtml = '<p class="hmc-store-locator__card-dist">' + mi.toFixed(1) + ' mi away</p>';
    }
    var addrLine = [r.city, r.state].filter(Boolean).join(', ');
    btn.innerHTML =
      '<p class="hmc-store-locator__card-name">' +
      escapeHtml(r.name || 'Installer') +
      (r.isNew ? '<span class="hmc-store-locator__card-new">New</span>' : '') +
      '</p><p class="hmc-store-locator__card-meta">' +
      escapeHtml(addrLine) +
      '</p>' +
      distHtml;
    btn.addEventListener('click', function () {
      Array.prototype.forEach.call(self.listEl.querySelectorAll('.hmc-store-locator__card'), function (c) {
        c.classList.remove('is-active');
      });
      btn.classList.add('is-active');
      self.focusInstaller(r);
    });
    self.listEl.appendChild(btn);
  };

  StoreLocatorRoot.prototype.renderList = function (opts) {
    opts = opts || {};
    var self = this;
    if (!self.listEl) return;

    self.syncListLayoutMode();

    if (self.usesPagedList()) {
      self.ensurePager();
      var filterKey = self.getListFilterKey();
      if (filterKey !== self._listFilterKey) {
        self._listFilterKey = filterKey;
        self.listPage = 0;
      }
    }

    var rows = self.getSortedFilteredRows();

    if (!rows.length) {
      self.listEl.classList.add('hmc-store-locator__list--empty');
      self.listEl.innerHTML =
        '<p class="hmc-store-locator__empty">No installers match your search or radius.</p>';
      if (self.pagerEl) self.pagerEl.hidden = true;
      self.scheduleMapResize();
      return;
    }

    self.listEl.classList.remove('hmc-store-locator__list--empty');

    var displayRows = rows;
    if (self.usesPagedList()) {
      var totalPages = Math.max(1, Math.ceil(rows.length / self.listPageSize));
      if (self.listPage >= totalPages) self.listPage = totalPages - 1;
      if (self.listPage < 0) self.listPage = 0;
      var start = self.listPage * self.listPageSize;
      displayRows = rows.slice(start, start + self.listPageSize);
      self.updatePager(rows.length, start, displayRows.length, totalPages);
    } else if (self.pagerEl) {
      self.pagerEl.hidden = true;
    }

    self.listEl.innerHTML = '';
    displayRows.forEach(function (r) {
      self.appendListCard(r);
    });

    if (opts.scroll && self.usesPagedList()) {
      self.scrollListIntoView(opts.scroll);
    }
    self.scheduleMapResize();
  };

  StoreLocatorRoot.prototype.scheduleMapResize = function () {
    var self = this;
    if (!self.map) return;
    function run() {
      try {
        self.map.resize();
      } catch (e) {}
    }
    run();
    window.requestAnimationFrame(run);
    setTimeout(run, 120);
  };

  function tryInit(root) {
    if (!root || root.dataset.hmcLocatorInit === '1') return;
    if (!isElementVisible(root)) return;
    var inst = new StoreLocatorRoot(root);
    inst.init();
  }

  function scan() {
    document.querySelectorAll('[data-hmc-store-locator]').forEach(function (root) {
      tryInit(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  document.addEventListener('hmc-store-locator-mounted', function () {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(scan);
    });
  });

  window.addEventListener('resize', function () {
    document.querySelectorAll('[data-hmc-store-locator]').forEach(function (root) {
      if (root._hmcMapResizeTimer) window.clearTimeout(root._hmcMapResizeTimer);
      root._hmcMapResizeTimer = window.setTimeout(function () {
        var map = root._hmcMapboxMap;
        if (map) {
          try {
            map.resize();
          } catch (e) {}
        }
        var inst = root._hmcStoreLocatorInstance;
        if (inst && inst.pagedList) {
          inst.syncListLayoutMode();
          inst.scheduleMapResize();
        }
      }, 200);
    });
  });

  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting && en.target) {
            tryInit(en.target);
          }
        });
      },
      { root: null, rootMargin: '120px 0px', threshold: 0.01 }
    );
    function observeAll() {
      document.querySelectorAll('[data-hmc-store-locator]').forEach(function (root) {
        io.observe(root);
      });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeAll);
    } else {
      observeAll();
    }
  }

  window.HmcStoreLocatorTryInit = tryInit;
})();
